import React,{useEffect,useMemo,useState} from 'react';
import {fetchStagingDashboard} from '../../stagingIntegration/client';
import './StagingBookings.css';

const tabs=['All','Upcoming','Awaiting completion','Completed','Cancelled'];
const today=()=>new Intl.DateTimeFormat('en-CA',{timeZone:'Europe/London',year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date());
const day=d=>String(d||'').slice(0,10);
const formatDate=d=>d?new Date(`${day(d)}T12:00:00Z`).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}):'—';
const money=n=>n==null||!Number.isFinite(Number(n))?'—':new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'}).format(Number(n));
const options=values=>[...new Set(values.filter(Boolean).map(String))].sort((a,b)=>a.localeCompare(b));
const paymentOption=b=>b.payType==='deposit'?'Deposit':b.payType==='payInAdvance'?'Full payment':'Not recorded';
const paymentLabel=b=>({paid:'Paid',refunded:'Refunded',pending:'Pending',failed:'Failed',cancelled:'Cancelled'}[b.paymentStatus]||b.paymentStatus||'Unknown');
const timeValue=t=>String(t||'').slice(0,5);
const appointmentEnd=b=>{
  if(!b.date||!b.endTime)return null;
  const date=day(b.date),time=timeValue(b.endTime);
  if(!/^\d{4}-\d{2}-\d{2}$/.test(date)||!/^\d{2}:\d{2}$/.test(time))return null;
  const zone=b.timeZone||'Europe/London';
  try {
    const target=`${date} ${time}`;
    const approx=Date.parse(`${date}T${time}:00Z`);
    for(let delta=-14*60;delta<=14*60;delta+=15){
      const candidate=new Date(approx+delta*60000);
      const parts=new Intl.DateTimeFormat('en-CA',{timeZone:zone,year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',hourCycle:'h23'}).formatToParts(candidate);
      const v=k=>parts.find(p=>p.type===k)?.value;
      if(`${v('year')}-${v('month')}-${v('day')} ${v('hour')}:${v('minute')}`===target)return candidate;
    }
  }catch(e){}
  return null;
};
const statusOf=b=>{
  if(['cancelled','refunded'].includes(b.status))return 'Cancelled';
  if(b.status==='finished')return 'Completed';
  const end=appointmentEnd(b);
  return end&&end.getTime()<=Date.now()?'Awaiting completion':'Upcoming';
};
const csvCell=x=>`"${String(x??'').replace(/"/g,'""')}"`;
const getToken=()=>{try{return JSON.parse(localStorage.getItem('admin')||'null')?.accessToken}catch(e){return null}};


let bsBookingsMemoryCache = null;

export default function StagingBookings({initialTab='All',initialDateRange=null}){
  const [rows, setRows] = useState(
    () => bsBookingsMemoryCache?.rows || []
  );
  const [loading, setLoading] = useState(
    () => !bsBookingsMemoryCache
  );
  const [error,setError]=useState('');
  const [total, setTotal] = useState(
    () => bsBookingsMemoryCache?.total || 0
  );
  const [tab,setTab]=useState(initialTab);
  useEffect(()=>setTab(initialTab),[initialTab]);
  const [term,setTerm]=useState('');
  const [provider,setProvider]=useState('');
  const [client,setClient]=useState('');
  const [payment,setPayment]=useState('');
  const [option,setOption]=useState('');
  const [basis,setBasis]=useState('appointment');
  const [period,setPeriod]=useState('all');
  const [from,setFrom]=useState('');
  const [to,setTo]=useState('');
  useEffect(()=>{
    if(initialDateRange?.from && initialDateRange?.to){
      setBasis('appointment'); setPeriod('custom'); setFrom(initialDateRange.from); setTo(initialDateRange.to);
    }
  },[initialDateRange]);
  const [sort,setSort]=useState('date-desc');
  const [selected,setSelected]=useState(null);
  const [finance,setFinance]=useState(null);
  const [financeLoading,setFinanceLoading]=useState(false);
  const [financeError,setFinanceError]=useState('');
  const [drawerTab,setDrawerTab]=useState('appointment');
  const [reminder,setReminder]=useState(false);
  const [tick,setTick]=useState(0);

  useEffect(()=>{
    let cancelled=false;
    async function load(){
      if (!bsBookingsMemoryCache) setLoading(true);setError('');
      try{
        const token=getToken();
        if(!token)throw Error('Sign in with an authorised staging administrator account.');
        const first=await fetchStagingDashboard('overview',{token,query:{drilldown:'bookingList',page:1},enabled:true});
        if(!first.success||!Array.isArray(first.rows))throw Error('Invalid booking response');
        if(first.total>10000)throw Error('Booking history exceeds the verified 10,000-record export limit. Contact the developer to enable server-side filtering.');
        let all=[...first.rows];
        const pages=Math.ceil(first.total/100);
        for(let p=2;p<=pages;p++){
          const next=await fetchStagingDashboard('overview',{token,query:{drilldown:'bookingList',page:p},enabled:true});
          if(!Array.isArray(next.rows))throw Error('Incomplete booking response');
          all.push(...next.rows);
        }
        if(all.length!==first.total)throw Error('Booking count changed during loading. Refresh to retry.');
        if(!cancelled){bsBookingsMemoryCache = {
          rows: all,
          total: first.total
        };
        setRows(all);setTotal(first.total);}
      }catch(e){if(!cancelled){setRows([]);setTotal(0);setError(e.message||'Bookings unavailable');}}
      finally{if(!cancelled)setLoading(false);}
    }
    load();
    return()=>{cancelled=true};
  },[tick]);

  useEffect(()=>{
    if (!selected || drawerTab!=='payments') return;
    let cancelled=false;
    setFinance(null);
    setFinanceError('');
    setFinanceLoading(true);

    const token=getToken();
    if(!token){
      setFinanceError('Administrator authentication unavailable');
      setFinanceLoading(false);
      return;
    }

    fetchStagingDashboard('overview',{
      token,
      enabled:true,
      query:{drilldown:"bookingFinance",bookingId:selected.id}
    }).then(result=>{
      if(cancelled)return;
      if(!result.success)throw Error(result.message||'Financial records unavailable');
      setFinance(result);
    }).catch(error=>{
      if(!cancelled)setFinanceError(error.message||'Unable to load financial records');
    }).finally(()=>{
      if(!cancelled)setFinanceLoading(false);
    });

    return()=>{cancelled=true};
  },[selected,drawerTab]);

  const providers=useMemo(()=>options(rows.map(b=>b.businessName||b.spName)),[rows]);
  const clients=useMemo(()=>options(rows.map(b=>b.name)),[rows]);
  const periodMatch=b=>{
    if(period==='all')return true;
    const d=basis==='confirmed'?day(b.createdAt||b.created_At||b.created_at):day(b.date);
    if(!d)return false;
    const now=today();
    if(period==='custom')return (!from||d>=from)&&(!to||d<=to);
    if(period==='today')return d===now;
    if(period==='7'||period==='30'){
      const start=new Date(`${now}T12:00:00Z`);
      start.setUTCDate(start.getUTCDate()-(Number(period)-1));
      return d>=start.toISOString().slice(0,10)&&d<=now;
    }
    return true;
  };
  const base=useMemo(()=>rows.filter(b=>{
    const q=term.trim().toLowerCase();
    const search=[b.id,b.uniqueId,b.name,b.businessName,b.spName,b.serviceName].join(' ').toLowerCase();
    return (!q||search.includes(q))&&
      (!provider||(b.businessName||b.spName)===provider)&&
      (!client||b.name===client)&&
      (!payment||paymentLabel(b)===payment)&&
      (!option||paymentOption(b)===option)&&periodMatch(b);
  }),[rows,term,provider,client,payment,option,basis,period,from,to]);
  const counts=useMemo(()=>Object.fromEntries(tabs.map(t=>[t,t==='All'?base.length:base.filter(b=>statusOf(b)===t).length])),[base]);
  const visible=useMemo(()=>base.filter(b=>tab==='All'||statusOf(b)===tab).sort((a,b)=>{
    if(sort==='amount-desc')return Number(b.grandTotal||0)-Number(a.grandTotal||0);
    if(sort==='amount-asc')return Number(a.grandTotal||0)-Number(b.grandTotal||0);
    const key=sort==='confirmed-desc'?'createdAt':'date';
    const value=x=>key==='createdAt'?String(x.createdAt||x.created_At||x.created_at||''):String(x.date||'');
    return (sort==='date-asc'?1:-1)*value(a).localeCompare(value(b))||(Number(b.id)-Number(a.id));
  }),[base,tab,sort]);
  const reset=()=>{setTerm('');setProvider('');setClient('');setPayment('');setOption('');setBasis('appointment');setPeriod('all');setFrom('');setTo('');setSort('date-desc');};
  const exportCsv=()=>{
    const data=[['Booking ID','Client','Provider','Service','Appointment date','Start','End','Time zone','Status','Payment status','Payment option','Recorded grand total GBP'],
      ...visible.map(b=>[b.uniqueId||b.id,b.name,b.businessName||b.spName,b.serviceName,b.date,b.startTime,b.endTime,b.timeZone||'Europe/London',statusOf(b),paymentLabel(b),paymentOption(b),b.grandTotal])];
    const blob=new Blob(['\ufeff'+data.map(r=>r.map(csvCell).join(',')).join('\r\n')],{type:'text/csv;charset=utf-8'});
    const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download='beautyspot-staging-bookings.csv';a.click();URL.revokeObjectURL(url);
  };
  const open=b=>{setSelected(b);setDrawerTab('appointment');setReminder(false)};
  const field=(label,value)=><div className="bsbk-field"><span>{label}</span><strong>{value||'—'}</strong></div>;
  const Select=({label,value,onChange,items})=><label>{label}<select value={value} onChange={e=>onChange(e.target.value)}>{items.map(([v,t])=><option key={v} value={v}>{t}</option>)}</select></label>;
  const list=(first,arr)=>[[ '',first ],...arr.map(x=>[x,x])];

  return <div className="bsbk-page">
    <header className="bsbk-heading"><h1>Bookings</h1><button onClick={exportCsv} disabled={loading||!!error}>Export filtered list ↗</button></header>
    <div className="bsbk-scope">Current booking status · Europe/London · Staging data <button onClick={()=>setTick(n=>n+1)}>Refresh</button></div>
    {error&&<div className="bsbk-error" role="alert">{error}</div>}
    <div className="bsbk-tabs">{tabs.map(t=><button key={t} className={tab===t?'active':''} onClick={()=>setTab(t)}>{t}<b>{loading?'—':counts[t]}</b></button>)}</div>
    <section className="bsbk-panel">
      <div className="bsbk-filters">
        <label className="bsbk-search">Search bookings<input value={term} onChange={e=>setTerm(e.target.value)} placeholder="Booking ID, client, provider or service"/></label>
        <Select label="Provider" value={provider} onChange={setProvider} items={list('All providers',providers)}/>
        <Select label="Client" value={client} onChange={setClient} items={list('All clients',clients)}/>
        <Select label="Payment status" value={payment} onChange={setPayment} items={list('All payment statuses',['Paid','Pending','Refunded','Failed','Cancelled','Unknown'])}/>
        <Select label="Payment option" value={option} onChange={setOption} items={list('All options',['Deposit','Full payment','Not recorded'])}/>
        <Select label="Date basis" value={basis} onChange={setBasis} items={[[ 'appointment','Appointment date' ],[ 'confirmed','Booking created' ]]}/>
        <Select label="Period" value={period} onChange={setPeriod} items={[[ 'all','All dates' ],[ 'today','Today' ],[ '7','Last 7 days' ],[ '30','Last 30 days' ],[ 'custom','Custom dates' ]]}/>
        {period==='custom'&&<><label>From<input type="date" value={from} onChange={e=>setFrom(e.target.value)}/></label><label>To<input type="date" value={to} onChange={e=>setTo(e.target.value)}/></label></>}
        <Select label="Sort by" value={sort} onChange={setSort} items={[[ 'date-desc','Appointment · newest first' ],[ 'date-asc','Appointment · oldest first' ],[ 'confirmed-desc','Most recently created' ],[ 'amount-desc','Highest recorded amount' ],[ 'amount-asc','Lowest recorded amount' ]]}/>
        <button className="bsbk-reset" onClick={reset}>Reset filters</button>
      </div>
      <div className="bsbk-result">{loading?'Loading staging bookings…':`${visible.length} appointments shown · ${basis==='appointment'?'Appointment date':'Booking creation date'} · ${period==='all'?'all dates':period}`}{!loading&&<small>{total} booking records loaded · Financial figures not wallet-reconciled</small>}</div>
      <div className="bsbk-scroll"><table><thead><tr><th>Booking</th><th>Client / provider</th><th>Appointment</th><th>Booking status</th><th>Payment</th><th>Wallet status</th></tr></thead>
        <tbody>{visible.map(b=><tr key={b.id}>
          <td><button className="bsbk-link" onClick={()=>open(b)}>{b.uniqueId||`#${b.id}`}</button><small>{b.serviceName||'Service not recorded'}</small>{statusOf(b)==='Awaiting completion'&&<button className="bsbk-link bsbk-remind" onClick={()=>{open(b);setReminder(true)}}>Send reminder ↗</button>}</td>
          <td>{b.name||'—'}<small>{b.businessName||b.spName||'—'}</small></td>
          <td>{formatDate(b.date)}<small>{timeValue(b.startTime)||'—'} · {b.timeZone||'Europe/London'}</small></td>
          <td><select value={statusOf(b)} disabled title="Status changes require a verified backend workflow">{tabs.slice(1).map(t=><option key={t}>{t}</option>)}</select></td>
          <td><strong>{money(b.grandTotal)}</strong><small>{paymentLabel(b)} · {paymentOption(b)}</small></td>
          <td><span className={`bsbk-wallet bsbk-wallet--${b.walletReleased?"released":statusOf(b)==="Awaiting completion"?"awaiting":statusOf(b)==="Upcoming"?"unreleased":String(b.status||"").toLowerCase()==="cancelled"?"cancelled":"unverified"}`}>{b.walletReleased?'Released':statusOf(b)==='Awaiting completion'?'Awaiting completion':statusOf(b)==='Upcoming'?'Not yet released':'Not verified'}</span></td>
        </tr>)}</tbody></table></div>
      {!loading&&!visible.length&&<div className="bsbk-empty">No bookings match these filters.</div>}
    </section>
    {selected&&<div className="bsbk-overlay" onMouseDown={()=>setSelected(null)}><aside className={`bsbk-drawer ${reminder ? "bsbk-reminder-drawer" : ""}`} onMouseDown={e=>e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Booking details">
      <button className="bsbk-close" onClick={()=>setSelected(null)}>×</button><div className="bsbk-eyebrow">BEAUTY SPOT ADMIN</div>
      {reminder?<><h2>Send completion reminder</h2><button className="bsbk-back" onClick={()=>setReminder(false)}>← Back</button>
        <p><b>Provider:</b> {selected.businessName||selected.spName||'—'}<br/><b>Booking:</b> {selected.uniqueId||selected.id} · {formatDate(selected.date)}<br/><b>Client:</b> {selected.name||'—'}</p>
        <h3>Push notification preview</h3><div className="bsbk-note"><b>BEAUTY SPOT · PUSH NOTIFICATION</b><h3>Appointment awaiting completion</h3>Please review booking {selected.uniqueId||selected.id}. If the service has finished, mark the appointment completed in Beauty Spot.</div>
        <p>Preview only. The existing push-notification workflow has not been verified.</p><button disabled>Send reminder · awaiting API connection</button>
      </>:<><h2>{selected.uniqueId||`Booking #${selected.id}`}</h2><button className="bsbk-back" onClick={()=>setSelected(null)}>← Back</button>
        <p>{selected.serviceName||'Service'} · {formatDate(selected.date)} · {timeValue(selected.startTime)||'—'}</p><p>{selected.name||'Client'} · {selected.businessName||selected.spName||'Provider'}</p>
        <div className="bsbk-summary">{field('Booking status',statusOf(selected))}{field('Payment status',paymentLabel(selected))}{field('Wallet status',selected.walletReleased?'Released to wallet':statusOf(selected)==='Awaiting completion'?'Awaiting completion':statusOf(selected)==='Upcoming'?'Not yet released':'Not verified')}</div>
        {statusOf(selected)==='Awaiting completion'&&
          <button className="bsbk-reminder-inline"
            onClick={()=>setReminder(true)}>Send reminder ↗</button>}
        <div className="bsbk-drawtabs">{[['appointment','Appointment'],['payments','Payments & wallet'],['history','History']].map(([k,t])=><button key={k} className={drawerTab===k?'active':''} onClick={()=>setDrawerTab(k)}>{t}</button>)}</div>
        {drawerTab==='appointment'&&<><h3>Appointment details</h3><div className="bsbk-fields">
          {field('Client',selected.name)}{field('Service provider',selected.businessName||selected.spName)}
          {field('Service',selected.serviceName)}{field('Appointment date',formatDate(selected.date))}
          {field('Start / end',`${timeValue(selected.startTime)||'—'} – ${timeValue(selected.endTime)||'—'} · ${selected.timeZone||'Europe/London'}`)}
          {field('Booking reference',selected.uniqueId||selected.id)}
          {field('Booking created',selected.createdAt||selected.created_At||selected.created_at||'Not recorded')}
          {field('Completion date','Not verified')}
        </div><div className="bsbk-note">Providers complete appointments after the scheduled end time. Wallet release is managed by the existing app workflow.</div></>}
        {drawerTab==='payments'&&<>
          {financeLoading&&<p>Loading booking payment and wallet records…</p>}
          {financeError&&<div className="bsbk-error">{financeError}</div>}
          {finance&&(()=>{
            const booking=finance.booking||{};
            const tx=finance.transactions||{available:false,rows:[]};
            const refunds=finance.refunds||{available:false,rows:[]};
            const revenue=finance.revenue||{available:false,rows:[]};

            const paid=tx.rows.filter(t=>
              t.paymentStatus==='paid' &&
              t.type==='deposit' &&
              t.paymentMode==='online'
            );

            const sum=(items,key)=>items.reduce(
              (total,item)=>total+Number(item[key]||0),0
            );

            const recordedPayments=tx.available&&paid.length
              ?money(sum(paid,'transactionAmount'))
              :'Not verified';

            const approvedRefunds=refunds.rows.filter(r=>
              r.status==='approved'&&r.paymentStatus==='paid'
            );

            const refundTotal=refunds.available&&approvedRefunds.length
              ?money(sum(approvedRefunds,'amount'))
              :'Not verified';

            const feeRows=revenue.rows.filter(r=>
              r.adminFee!==null &&
              r.adminFee!==undefined &&
              Number.isFinite(Number(r.adminFee))
            );

            const feeTotal=feeRows.length
              ?money(sum(feeRows,'adminFee'))
              :'Not recorded';

            // Read-only wallet release evidence.
            // A finished booking alone does not prove a release.
            const transfers=tx.rows.filter(t=>
              t.type==='transfer' &&
              t.transactionId &&
              Number(t.transactionAmount)>0
            );

            const releasedRevenue=revenue.rows.filter(r=>
              r.amount!==null &&
              r.amount!==undefined &&
              Number(r.amount)>0
            );

            const bookingStatus=String(booking.status||'').toLowerCase();

            const transferAmount=sum(transfers,'transactionAmount');
            const revenueAmount=sum(releasedRevenue,'amount');

            const amountsAgree=
              Math.abs(transferAmount-revenueAmount)<0.02;

            const releaseVerified=
              bookingStatus==='finished' &&
              tx.available &&
              transfers.length>0;

            const refundPending=refunds.rows.some(r=>
              String(r.paymentStatus||'').toLowerCase()==='pending' &&
              String(r.status||'').toLowerCase()!=='rejected'
            );

            const walletStatus=
              bookingStatus==='cancelled' ||
              bookingStatus==='refunded'
                ? refundPending
                  ? 'Refund processing'
                  : 'Cancelled / refunded'
                : releaseVerified
                  ? 'Released to wallet'
                  : statusOf(selected)==='Awaiting completion'
                    ? 'Awaiting completion'
                    : bookingStatus==='finished'
                      ? 'Release not verified'
                      : 'Not yet released';

            const releasedWalletCredit=releaseVerified
              ?money(transferAmount)
              :'Not verified';

            const row=(label,value)=>
              <div key={label}><span>{label}</span><strong>{value}</strong></div>;

            return <>
              <h3>Price &amp; payment</h3>
              <div className="bsbk-financial-table">
                {row('Full service price',money(booking.price))}
                {row('Payment option',paymentOption(booking))}
                {row('Recorded online payments',recordedPayments)}
                {row('Balance payable directly to provider',
                  booking.remainingAmount==null?'Not recorded':money(booking.remainingAmount))}
                {row('Recorded approved refunds',refundTotal)}
              </div>

              <h3>Wallet release &amp; fees</h3>
              <div className="bsbk-financial-table">
                {row('Wallet status',walletStatus)}
                {row('Beauty Spot fee · recorded',feeTotal)}
                {row('Released provider wallet credit',releasedWalletCredit)}
                {row('Expected credit after completion','Not verified')}
              </div>

              <div className="bsbk-note">
                Beauty Spot's 5% transaction fee includes Stripe processing.
                No additional Beauty Spot VAT or Stripe fee is added.
                Recorded fees and transactions have not been independently
                reconciled with Stripe or wallet releases.
              </div>

              <h3>Refund history</h3>
              {!refunds.available
                ?<p>Refund records unavailable.</p>
                :refunds.rows.length===0
                  ?<p>No refund records linked to this booking.</p>
                  :<div className="bsbk-financial-table">
                    {refunds.rows.map(r=>
                      <div key={r.id}>
                        <span>Refund #{r.id} · {r.status||'Unknown status'} · {r.paymentStatus||'Unknown payment status'}</span>
                        <strong>{money(r.amount)}</strong>
                      </div>
                    )}
                  </div>
              }

              <div className="bsbk-note">
                Provider wallet release remains unverified.
                Revenue payAmount is not treated as proof that funds
                were credited or withdrawn.
              </div>
            </>;
          })()}
        </>}
        {drawerTab==='history'&&<><h3>History</h3><div className="bsbk-note">Booking creation: {selected.createdAt||selected.created_At||selected.created_at||'Not recorded'}. A verified status-change audit trail is not yet connected.</div></>}

        <div className="bsbk-note">Read-only staging records. Status changes, refunds, transfers and notifications are disabled.</div>
      </>}
    </aside></div>}
  </div>;
}
